import { useState } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Shield, Eye, Lock, UserX, AlertTriangle, MessageCircle, VolumeX } from 'lucide-react';
import { Switch } from './ui/switch';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { useUserData } from './UserDataContext';
import { EVENTS, SCREENS } from '../constants/navigation';
import { ParallaxBackground } from './utils/parallax';

interface SafetySettingsScreenProps {
  onBack: () => void;
}

export function SafetySettingsScreen({ onBack }: SafetySettingsScreenProps) {
  const { userProfile, updateProfile, mutedWords } = useUserData();
  const [settings, setSettings] = useState({
    privateProfile: false,
    hideLocation: false,
    onlyMatches: true,
    readReceipts: true,
    safeMode: true,
  });

  // These dispatch the generic `navigateToScreen` bridge that AppRouter actually
  // listens on. The bespoke 'navigateToBlockedUsers' / 'navigateToReport' events
  // used before had no listener anywhere, so both buttons silently did nothing
  // even though their routes existed.
  const goToScreen = (screen: string) => {
    window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SCREEN, { detail: screen }));
  };

  const handleViewBlockedUsers = () => goToScreen(SCREENS.BLOCKED_USERS);
  const handleViewMutedWords = () => goToScreen(SCREENS.MUTED_WORDS);
  const handleReportProblem = () => goToScreen(SCREENS.REPORT);

  const toggleSetting = (key: keyof typeof settings) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleDMPrivacy = () => {
    const newPrivacy = userProfile.dmPrivacy === 'friends' ? 'everyone' : 'friends';
    updateProfile({ dmPrivacy: newPrivacy });
  };

  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div
          className="p-4 border-b border-black/10 dark:border-white/10 bg-light-pattern dark:bg-[#121212]"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
        >
          <div className="flex items-center gap-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="text-black dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-white"
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <div>
              <h1 className="text-3xl text-black dark:text-white">Safety & Privacy</h1>
              <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">Manage your security settings</p>
            </div>
          </div>
        </div>

        <ScrollArea className="flex-1" style={{ minHeight: 0 }}>
          <div className="p-6 space-y-6">
            {/* Privacy Section */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                <h2 className="text-xl text-black dark:text-white">Privacy Settings</h2>
              </div>
              <div className="space-y-4">
                {/* Direct Message Privacy */}
                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#2C3E50] border border-black/10 dark:border-white/10 card-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageCircle className="w-4 h-4 text-[#F39C12] dark:text-[#4478ea]" />
                        <h3 className="text-black dark:text-white">Who Can Message You</h3>
                      </div>
                      <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">
                        {userProfile.dmPrivacy === 'friends' ? 'Only friends can send you direct messages' : 'Everyone can send you direct messages'}
                      </p>
                    </div>
                    <Switch
                      checked={userProfile.dmPrivacy === 'friends'}
                      onCheckedChange={toggleDMPrivacy}
                    />
                  </div>
                  <div className="mt-3 pt-3 border-t border-black/10 dark:border-white/10">
                    <p className="text-xs text-[#999999] dark:text-white">
                      {userProfile.dmPrivacy === 'friends'
                        ? '?? Friends Only - More private and secure'
                        : '?? Everyone - More open to new connections'}
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#2C3E50] border border-black/10 dark:border-white/10 card-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-black dark:text-white mb-1">Private Profile</h3>
                      <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">Only matches can see your profile</p>
                    </div>
                    <Switch
                      checked={settings.privateProfile}
                      onCheckedChange={() => toggleSetting('privateProfile')}
                    />
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#2C3E50] border border-black/10 dark:border-white/10 card-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-black dark:text-white mb-1">Hide Location</h3>
                      <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">Don't show your precise location</p>
                    </div>
                    <Switch
                      checked={settings.hideLocation}
                      onCheckedChange={() => toggleSetting('hideLocation')}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Messaging Section */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                <h2 className="text-xl text-black dark:text-white">Messaging</h2>
              </div>
              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#2C3E50] border border-black/10 dark:border-white/10 card-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-black dark:text-white mb-1">Only Matches Can Message</h3>
                      <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">Block messages from non-matches</p>
                    </div>
                    <Switch
                      checked={settings.onlyMatches}
                      onCheckedChange={() => toggleSetting('onlyMatches')}
                    />
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#2C3E50] border border-black/10 dark:border-white/10 card-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-black dark:text-white mb-1">Read Receipts</h3>
                      <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">Let others know when you read messages</p>
                    </div>
                    <Switch
                      checked={settings.readReceipts}
                      onCheckedChange={() => toggleSetting('readReceipts')}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Safety Section */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                <h2 className="text-xl text-black dark:text-white">Safety Features</h2>
              </div>
              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#2C3E50] border border-black/10 dark:border-white/10 card-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-black dark:text-white mb-1">Safe Mode</h3>
                      <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">Filter inappropriate content automatically</p>
                    </div>
                    <Switch
                      checked={settings.safeMode}
                      onCheckedChange={() => toggleSetting('safeMode')}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Action Buttons */}
            <section className="space-y-3">
              <Button
                variant="outline"
                className="w-full h-14 bg-transparent border-2 border-black/20 dark:border-white/20 text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/10 rounded-xl"
                onClick={handleViewBlockedUsers}
              >
                <Eye className="mr-2 w-5 h-5" />
                View Blocked Users
              </Button>
              <Button
                variant="outline"
                className="w-full h-14 bg-transparent border-2 border-black/20 dark:border-white/20 text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/10 rounded-xl"
                onClick={handleViewMutedWords}
              >
                <VolumeX className="mr-2 w-5 h-5" />
                Muted Words
                {mutedWords.length > 0 && (
                  <span className="ml-2 text-xs text-[#666666] dark:text-[#a0a0a0]">
                    {mutedWords.length}
                  </span>
                )}
              </Button>
              <Button
                variant="outline"
                className="w-full h-14 bg-transparent border-2 border-[#E74C3C] dark:border-[#2d1ef1] text-[#E74C3C] dark:text-[#4478ea] hover:bg-[#E74C3C] dark:hover:bg-[#2d1ef1] hover:text-white rounded-xl transition-all"
                onClick={handleReportProblem}
              >
                <UserX className="mr-2 w-5 h-5" />
                Report a Problem
              </Button>
            </section>

            {/* Info */}
            <div className="p-4 rounded-2xl bg-linear-to-r from-[#F39C12]/10 to-[#E74C3C]/10 dark:from-[#4478ea]/10 dark:to-[#2d1ef1]/10 border border-[#F39C12]/30 dark:border-[#4478ea]/30">
              <p className="text-sm text-black dark:text-[#E5E5E5] leading-relaxed">
                Your safety is our priority. AfroYap is committed to creating a secure and respectful community.
                Report any suspicious behavior or content immediately.
              </p>
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
